/**
 * Browser-level autofill suppression helper (18/02/2026).
 *
 * Operational forms in the gestionale must NEVER show Edge / Chrome
 * personal-memory suggestions (saved emails, "Gestisci sito personale",
 * profile data, saved phone numbers, ecc.). In a shared-office /
 * client-facing environment this creates UI confusion, wrong-data
 * insertion risk, and a privacy concern.
 *
 * Browsers routinely IGNORE plain `autocomplete="off"`. The
 * combination below covers Edge, Chrome, Safari, Firefox AND the
 * common password managers (LastPass, 1Password, Bitwarden):
 *
 *   - autoComplete  : non-standard random token defeats heuristic
 *                     field-type detection that triggers personal
 *                     autofill overlays.
 *   - autoCorrect/  : prevents iOS Safari auto-correction popups
 *     spellCheck      that look like suggestions.
 *   - data-form-type: Edge-specific opt-out (renamed from
 *                     `data-no-form-fill` in 2024+).
 *   - data-lpignore : LastPass opt-out.
 *   - data-1p-ignore: 1Password opt-out.
 *   - data-bwignore : Bitwarden opt-out.
 *
 * Usage:
 *   import { noAutofillProps } from "@/lib/no-autofill";
 *   <Input {...noAutofillProps} value={x} onChange={...} />
 *
 * DO NOT apply to:
 *   - Password-change dialogs (current/new password fields).
 *   - Internal app autocomplete widgets (Combobox cliente, ecc.) —
 *     those don't use a native <input> as the suggestion source.
 *
 * APPLY to (added 20/02/2026):
 *   - Login page email/username field. In office/shared PCs the
 *     Edge password manager was exposing unrelated personal emails
 *     of all the users of that workstation. The app keeps offering
 *     its OWN suggestion via `LAST_EMAIL_STORAGE_KEY` localStorage,
 *     so the operator still only types the password after a lock.
 *     The login password field stays on `autoComplete="new-password"`
 *     which already opts out of the password manager save prompt.
 */

export const noAutofillProps = Object.freeze({
  autoComplete: "off-no-suggest",
  autoCorrect: "off",
  spellCheck: false,
  "data-form-type": "other",
  "data-lpignore": "true",
  "data-1p-ignore": "true",
  "data-bwignore": "true",
});

/**
 * Same intent but for `<form>` wrappers. FastAPI / browser engines
 * honour the form-level hint as a fallback when individual inputs
 * forget to opt-out.
 */
export const noAutofillFormProps = Object.freeze({
  autoComplete: "off",
  "data-form-type": "other",
});
